import { Container } from "components/shared";
import { Button, Skeleton, Spinner, Tooltip } from "components/ui";
import { CompanyContext } from "contexts/CompanyContext";
import { useContext, useEffect, useRef, useState } from "react";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { api as Api } from "api/http";
import MediaElement from "views/calendar/MediaElement";
import AIImageDropdownSizes from "./components/AIImageDropdownSizes";
import InputModern from "components/ui/Input/InputModern";
import useRandomContexts from "utils/hooks/useRandomContexts";
import { getAspectRatio } from "utils/getAspectRatio";
import AIImageDropdownStyles from "./components/AIImageDropdownStyles";
import { aiImageStyles } from "views/company_settings/components/AIImages";
import AIImageDropdownModels from "./components/AIImageDropdownModels";
import { AIContext, aiSizeOptions } from "contexts/AIContext";
import AIImageGenerateButton from "./components/AIImageGenerateButton";
import { useWhitelabel } from "contexts/WhitelabelThemeContext";
import { BsImage, BsPalette, BsMagic, BsX } from "react-icons/bs";
import { HiOutlinePlus } from "react-icons/hi2";
import AIImageInspirationDropdown from "./components/AIImageInspirationDropdown";
import { ImageSelector } from "components/image-chat";
import UpgradeBanner from "components/shared/UpgradeBanner";
import EmptyState from "./components/EmptyState";

const AIImageBody = ({
    inputClassName = "",
    columnBreakPoints = { 0: 1, 768: 2, 1100: 3, 1400: 4 }, // 3 columns on xl, 2 on normal, 1 on tablet/mobile
    showPreview = true,
    handleOnSelect = undefined,
    handleOnUnselect = undefined,
    selectedImages = undefined, 
    isSelectable = undefined,
    simplificated = false,
    showCreate = true
}) => {
    
    const { company } = useContext(CompanyContext);
    const { getImages, getDriverModel, getByQueryChat, aiModels } = useContext(AIContext);
    const { generateRandomAIImagePrompts } = useRandomContexts();
    const [ query, setQuery ] = useState("");
    const [ model, setModel ] = useState(getDriverModel("image")?.model);
    const [ n, setN ] = useState(1);
    const [ size, setSize ] = useState(aiSizeOptions[0].id);
    const [ style, setStyle ] = useState(aiImageStyles[0].style);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ isLoadingSuggestions, setIsLoadingSuggestions ] = useState(false);
    const [ isLoadingHistory, setIsLoadingHistory ] = useState(true);
    const [ fixedQuery, setFixedQuery ] = useState();
    const [ historyImages, setHistoryImages ] = useState();
    const [ suggestions, setSuggestions ] = useState([]);
    const [ nextLink, setNextLink ] = useState();
    const [ selectedInputImages, setSelectedInputImages ] = useState([]); // For image input
    const [ showImageSelector, setShowImageSelector ] = useState(false);
    const inputRef = useRef();
    const { whitelabel } = useWhitelabel();

    // Check if current model has isJob property and image editing capabilities
    const currentImageModel = aiModels?.find(m => m.model === model && m.type === 'image');
    const isJobBasedModel = currentImageModel?.isJob === true;
    const allowsImageEdit = currentImageModel?.allowImageEdit === true;
    const maxInputImages = currentImageModel?.maxInputImages ? currentImageModel?.maxInputImages : 0;
    const supportsMultipleImages = maxInputImages > 1;

    const getHistoryImages = (replace = true) => {
        setIsLoadingHistory(true);
        Api.get(!replace && nextLink ? nextLink : `/aiResults?include=images,images.thumbnail,user,user.image&sort=-created_at&filter[company_id]=${company?.id}&filter[type]=image`).then(({ data: results, links }) => {
            setHistoryImages(replace ? results : [...historyImages, ...results]);
            setNextLink(links?.next);
        }).catch((err) => {
            console.error(err);
        }).finally(() => {
            setIsLoadingHistory(false);
        });
    }

    const _removeSavedSuggestion = (_query) => {
        let savedSuggestions = suggestions;
        savedSuggestions = savedSuggestions.filter(suggestion => suggestion?.toLowerCase() != _query?.toLowerCase());
        setSuggestions(savedSuggestions);
        localStorage.setItem(`aiImage.suggestions.${company?.id}`, JSON.stringify(savedSuggestions));
    }

    const generateImages = async (n = 1) => {
        let _query = query;
        _removeSavedSuggestion(_query)
        setFixedQuery("");
        setN(n);
        setIsLoading(true);
        let _style = style;
        if (!_style) _style = aiImageStyles.map(e => e.style).random();
        const modelObj = aiModels.filter(e => e.type == "image").find(e => e.model == model);
        if (modelObj?.needsTranslation) {
            _query = await getByQueryChat(`Translate this text to English or keep it the same if it's already in English: ${_query}
                Translated text: `, false, true, "");
        }
        _query = `${_style != "No style" ? `${_style} image: ` : ``}${_query}`;
        
        // Prepare image URLs for editing
        const imageUrls = selectedInputImages.map(img => img.originalSrc);
        
        setQuery("");
        setSelectedInputImages([]); // Clear selected images after generation
        
        await Promise.allSettled([...Array(n).keys()].map(e => getImages(_query, size, model, false, imageUrls)));
        getHistoryImages();
        setIsLoading(false);
    }

    const initialSuggestions = async (regenerate = false) => {
        setIsLoadingSuggestions(true);
        setSuggestions(
            !!localStorage.getItem(`aiImage.suggestions.${company?.id}`) && !regenerate 
                ? JSON.parse(localStorage.getItem(`aiImage.suggestions.${company?.id}`))
                : await generateRandomAIImagePrompts(undefined, !!suggestions?.length)
        );
        setIsLoadingSuggestions(false);
    }

    const resize = () => {
      inputRef.current.style.cssText = 'height: 100px !important';
      inputRef.current.style.cssText = `height: ${Math.max(inputRef.current.scrollHeight, 0)}px !important`;
    }

    useEffect(() => {
        if (!!suggestions?.length) {
            localStorage.setItem(`aiImage.suggestions.${company?.id}`, JSON.stringify(suggestions));
        }
    }, [suggestions]);

    useEffect(() => {
        getHistoryImages();
        initialSuggestions();
    }, [company?.id]);
    
    useEffect(() => {
        setModel(getDriverModel("image")?.model);
    }, [company?.id, company?.ai_preferences]);
  
    useEffect(() => {
      resize();
    }, [query]);

    useEffect(() => {
        if (!!model && !aiModels?.find(m => m.model == model)?.availableSizes?.includes(size)) {
            setSize(aiSizeOptions?.filter(e => aiModels?.find(m => m.model == model)?.availableSizes?.includes(e.id))?.[0]?.id);
        }
    }, [model]);

    const handleSelectImages = (images) => {
        setSelectedInputImages(images.map(img => ({
            originalSrc: img.originalSrc,
            asset: img.asset
        })));
        setShowImageSelector(false);
    };

    const removeSelectedImage = (originalSrc) => {
        setSelectedInputImages(prev => prev.filter(img => img.originalSrc !== originalSrc));
    };

    // Reset selected images when model changes to one that doesn't support image editing
    useEffect(() => {
        if (!allowsImageEdit && selectedInputImages.length > 0) {
            setSelectedInputImages([]);
        }
    }, [allowsImageEdit]);

    const handleScrollToCreate = () => {
        // Scroll to the input area
        if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    return (
        <div className={`w-full h-full ${simplificated ? 'flex flex-col' : 'flex flex-col'} gap-8`}>
            {/* Upgrade Banner */}
            <UpgradeBanner page="ai-image" />
            
            {/* Empty State - Show when no images and not loading */}
            {!isLoadingHistory && !historyImages?.length && !isLoading && (
                <EmptyState onCreateClick={handleScrollToCreate} />
            )}
            
            {/* Revolutionary Centered Creation Studio */}
            <div className={`flex flex-col flex-1 justify-center items-center ${!showCreate && `!hidden`}`}>
                <div className={`${simplificated ? 'flex-1' : 'flex-[0.54]'} flex justify-center relative z-[11] max-w-full w-full`}>
                    <div className="w-full mx-auto">
                        {/* Main Creation Card */}
                        <div className={`relative group`}>
                            {/* Floating Background Elements */}
                            <div className="absolute -inset-1 bg-gradient-to-r from-pink-600 via-orange-600 to-rose-600 rounded-2xl blur-lg opacity-20 group-hover:opacity-30 transition-all duration-700 animate-pulse"></div>
                            <div className="absolute -inset-0.5 bg-gradient-to-r from-pink-500 via-orange-500 to-rose-500 rounded-2xl opacity-0 group-hover:opacity-20 transition-all duration-500"></div>
                            
                            {/* Main Card */}
                            <div className="relative bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-white/20 dark:border-gray-700/30 shadow-2xl shadow-black/5 dark:shadow-black/20">
                                
                                {/* Header Section */}
                                <div className="relative p-6">
                                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-pink-500/10 via-orange-500/10 to-rose-500/10 dark:from-pink-500/5 dark:via-orange-500/5 dark:to-rose-500/5 rounded-t-2xl"></div>
                                    <div className="relative">
                                        <div className="flex items-center h-full gap-4">
                                            <div className="relative">
                                                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg shadow-pink-500/25">
                                                    <BsImage className="w-6 h-6 text-white" />
                                                </div>
                                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-rose-400 rounded-full animate-ping"></div>
                                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-rose-400 rounded-full"></div>
                                            </div>
                                            <div>
                                                <div className="text-lg font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                                                    AI Image Studio
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    Transform your ideas into stunning images
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Input Section */}
                                <div className="p-6 pb-0">
                                    <div className="relative">
                                        <InputModern
                                            fieldsetClassName="!bg-transparent !border-0"
                                            className="!bg-gradient-to-br from-gray-50/80 to-white/80 dark:!from-gray-800/80 dark:!to-gray-900/80 !border-0 !shadow-[inset_0_2px_8px_0_rgba(17,24,39,0.03),inset_0_-2px_8px_0_rgba(17,24,39,0.03),inset_2px_0_8px_0_rgba(17,24,39,0.03),inset_-2px_0_8px_0_rgba(17,24,39,0.03)] dark:!shadow-[inset_0_2px_8px_0_rgba(0,0,0,0.1),inset_0_-2px_8px_0_rgba(0,0,0,0.1),inset_2px_0_8px_0_rgba(0,0,0,0.1),inset_-2px_0_8px_0_rgba(0,0,0,0.1)] rounded-2xl !px-6 !py-5 !pb-16 min-h-[126px] max-h-[300px] overflow-auto resize-none !text-base placeholder:!text-gray-400 dark:placeholder:!text-gray-500 focus:!ring-0 focus:!shadow-lg focus:!shadow-pink-500/10 transition-all duration-300" 
                                            textArea={true}
                                            value={query}
                                            ref={inputRef}
                                            placeholder="Describe your vision... What image should we create for you?" 
                                            onChange={(e) => setQuery(e.target.value)} 
                                            onKeyUp={(e) => e.key == 'Enter' ? generateImages() : void 0}
                                        />
                                        
                                        {/* Floating Controls */}
                                        <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                                            {/* Left side - Image Input Controls */}
                                            <div className="flex items-center gap-2">
                                                {allowsImageEdit && (
                                                    <>
                                                        {/* Show selected images for models that support multiple images */}
                                                        {supportsMultipleImages && selectedInputImages.map((image, index) => (
                                                            <Tooltip key={image.originalSrc} title="Remove image">
                                                                <div className="relative w-8 h-8 rounded-lg overflow-hidden border-2 border-pink-400 hover:border-pink-500 transition-all duration-200">
                                                                    <img 
                                                                        src={image.originalSrc} 
                                                                        alt={`Reference ${index + 1}`} 
                                                                        className="w-full h-full object-cover" 
                                                                    />
                                                                    <div
                                                                        className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity duration-200"
                                                                        onClick={() => removeSelectedImage(image.originalSrc)}
                                                                    >
                                                                        <BsX className="w-3 h-3 text-white" />
                                                                    </div>
                                                                </div>
                                                            </Tooltip>
                                                        ))}
                                                        
                                                        {/* Add image button - always show for multiple image models, or show for single image models when no image is selected */}
                                                        {(supportsMultipleImages && selectedInputImages.length < maxInputImages) || (!supportsMultipleImages && selectedInputImages.length === 0) ? (
                                                            <Tooltip title={`Add reference image${supportsMultipleImages ? 's' : ''} (${selectedInputImages.length}/${maxInputImages})`}>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setShowImageSelector(true)}
                                                                    className="relative w-8 h-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden flex items-center justify-center hover:border-pink-400 dark:hover:border-pink-500 hover:bg-pink-50 dark:hover:bg-pink-900/20 transition-all duration-200"
                                                                >
                                                                    <HiOutlinePlus className="w-3 h-3 text-gray-400 dark:text-gray-500 hover:text-pink-500 dark:hover:text-pink-400 transition-colors" />
                                                                </button>
                                                            </Tooltip>
                                                        ) : null}
                                                        
                                                        {/* For single image models, show the selected image with remove overlay */}
                                                        {!supportsMultipleImages && selectedInputImages.length > 0 && (
                                                            <Tooltip title="Remove reference image">
                                                                <div className="relative w-8 h-8 border-2 border-pink-400 rounded-lg overflow-hidden">
                                                                    <img 
                                                                        src={selectedInputImages[0].originalSrc} 
                                                                        alt="Reference" 
                                                                        className="w-full h-full object-cover" 
                                                                    />
                                                                    <div
                                                                        className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity duration-200"
                                                                        onClick={() => setSelectedInputImages([])}
                                                                    >
                                                                        <BsX className="w-3 h-3 text-white" />
                                                                    </div>
                                                                </div>
                                                            </Tooltip>
                                                        )}
                                                    </>
                                                )}
                                            </div>

                                            {/* Right side - Model, Style, Size, and Inspiration controls */}
                                            <div className="flex items-center gap-2">
                                                {/* Model Dropdown */}
                                                <AIImageDropdownModels 
                                                    model={model}
                                                    setModel={setModel}
                                                    parentClassName="flex-1"
                                                    className="!bg-white/80 dark:!bg-gray-800/80 !border-white/50 dark:!border-gray-700/50 !backdrop-blur-sm rounded-lg !px-2 !py-2 !text-xs !min-w-[50px] !h-auto hover:!bg-white/90 dark:hover:!bg-gray-800/90 transition-all duration-300 !text-gray-600 dark:!text-gray-400 !shadow"
                                                />

                                                {/* Style Dropdown */}
                                                {/* <AIImageDropdownStyles 
                                                    style={style}
                                                    setStyle={setStyle}
                                                    parentClassName="relative"
                                                    className="!bg-white/80 dark:!bg-gray-800/80 !border-white/50 dark:!border-gray-700/50 !backdrop-blur-sm rounded-lg !px-1 !py-2 !text-xs !min-w-[50px] !h-auto hover:!bg-white/90 dark:hover:!bg-gray-800/90 transition-all duration-300 !text-gray-600 dark:!text-gray-400 !shadow"
                                                /> */}

                                                {/* Size Dropdown */}
                                                <AIImageDropdownSizes 
                                                    size={size}
                                                    setSize={setSize}
                                                    model={model}
                                                    parentClassName="relative"
                                                    className="!bg-white/80 dark:!bg-gray-800/80 !border-white/50 dark:!border-gray-700/50 !backdrop-blur-sm rounded-lg !px-1 !py-2 !text-xs !min-w-[50px] !h-auto hover:!bg-white/90 dark:hover:!bg-gray-800/90 transition-all duration-300 !text-gray-600 dark:!text-gray-400 !shadow"
                                                />

                                                <AIImageInspirationDropdown 
                                                    setQuery={setQuery}
                                                    className="hover:!scale-105"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Generate Button */}
                                <div className="px-6 py-4 pb-6">
                                    <AIImageGenerateButton 
                                        isLoading={isLoading}
                                        query={query}
                                        generateImages={generateImages}
                                        model={model}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {(!!historyImages?.length || isLoading || isLoadingHistory) && 
                <Container className={`relative ${simplificated ? 'flex-1' : 'flex-1 overflow-y-auto'} min-h-full`}>
                    <div className={`relative flex flex-col gap-8 shrink-0 max-w-full ${simplificated ? 'lg:max-w-full' : 'lg:max-w-[250px]'} max-h-[512px] w-full ${1 == 1 && `!max-w-none !w-full !max-h-none`}`}>
                        {isLoadingHistory && !isLoading && !historyImages?.length
                            ? (simplificated 
                                ? <div className="flex flex-col gap-4">
                                    {[1,2,3,4,5].map(r => (
                                        <div key={r} className="relative flex items-center justify-center aspect-[16/9]">
                                            <Skeleton className="w-full h-full absolute left-0 top-0 rounded-lg" />
                                        </div>
                                    ))}
                                  </div>
                                : <ResponsiveMasonry columnsCountBreakPoints={columnBreakPoints} className={`w-full transition-all ${1 == 1 && `lg:!w-full lg:!max-w-none lg:!max-h-none`}`}>
                                    <Masonry gutter='0.5rem'>
                                        {[1,2,4,5,3,2,1].map(r => (
                                            <div className={`relative flex items-center justify-center ${r % 3 == 0 ? `!aspect-square` : r % 3 == 1 ? `!aspect-[16/9]` : `!aspect-[9/16]`}`}>
                                                <Skeleton className={`w-full h-full absolute left-0 top-0 rounded-lg`} />
                                            </div>
                                        ))}
                                    </Masonry>
                                </ResponsiveMasonry>
                            )
                            : (simplificated
                                ? <div className="flex flex-col gap-4">
                                    {isLoading && 
                                        <div className="relative flex items-center justify-center aspect-[16/9]">
                                            <Skeleton className="w-full h-full absolute left-0 top-0 rounded-lg" />
                                                                                            <div className="flex flex-col items-center justify-center gap-2">
                                                    <Spinner size={40} className="text-pink-600" />
                                                    {isJobBasedModel && (
                                                        <div className='mt-2 p-3 bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 rounded-lg max-w-xs'>
                                                            <p className='text-xs text-pink-700 dark:text-pink-300 text-center'>
                                                                <strong>High-quality AI image generation in progress.</strong><br />
                                                                This may take a few minutes.
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                        </div>
                                    }
                                    {historyImages?.map((result, key) => {
                                        result = {
                                            ...result,
                                            images: result?.images?.filter(e => e?.status == "analyzed")
                                        };
                                        const image = result?.images?.[0];
                                        const imageURL = result?.images?.[0]?.url || result?.response?.split(",")?.[0];
                                        const imageThumbnailURL = result?.images?.[0]?.thumbnail?.url || imageURL;
                                        return (
                                            <span 
                                                key={key}
                                                className={`${!!showPreview && `transition-all overflow-hidden`}`}
                                            >
                                                <MediaElement 
                                                    isStock={true}
                                                    isDraggable={true}
                                                    key={imageURL}
                                                    src={imageThumbnailURL}
                                                    originalSrc={imageURL}
                                                    handleOnSelect={handleOnSelect}
                                                    handleOnUnselect={handleOnUnselect}
                                                    selected={!!selectedImages?.find(e => e.originalSrc == imageURL)} 
                                                    isSelectable={isSelectable}
                                                    aspectRatio="16/9"
                                                    isEditable={false}
                                                    mediaDescription={`Prompt: "${result?.query}"`}
                                                    aiImageModel={result?.model}
                                                    user={result?.user}
                                                    onError={(e) => {
                                                        try { e.target.parentElement.parentElement.style = "display: none" } catch (err) {}
                                                    }}
                                                />
                                            </span>
                                        );
                                    })}
                                  </div>
                                : <ResponsiveMasonry columnsCountBreakPoints={columnBreakPoints} className={`w-full transition-all ${1 == 1 && `lg:!w-full lg:!max-w-none lg:!max-h-none`}`}>
                                    <Masonry gutter='1.5rem'>
                                        {isLoading && 
                                            <div className="relative flex items-center justify-center !aspect-square">
                                                <Skeleton className={`w-full h-full absolute left-0 top-0 rounded-lg`} />
                                                <div className="flex flex-col items-center justify-center gap-2">
                                                    <Spinner size={40} className="text-pink-600" />
                                                    {isJobBasedModel && (
                                                        <div className='mt-2 p-2 bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 rounded-lg max-w-xs'>
                                                            <p className='text-xs text-pink-700 dark:text-pink-300 text-center'>
                                                                <strong>High-quality generation in progress.</strong><br />
                                                                Please wait a few minutes.
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        }
                                        {isLoading && n > 1 && 
                                            <div className="relative flex items-center justify-center !aspect-square">
                                                <Skeleton className={`w-full h-full absolute left-0 top-0 rounded-lg`} />
                                                <Spinner size={40} className="text-pink-600" />
                                            </div>
                                        }
                                        {isLoading && n > 2 && 
                                            <div className="relative flex items-center justify-center !aspect-square">
                                                <Skeleton className={`w-full h-full absolute left-0 top-0 rounded-lg`} />
                                                <Spinner size={40} className="text-pink-600" />
                                            </div>
                                        }
                                        {isLoading && n > 3 && 
                                            <div className="relative flex items-center justify-center !aspect-square">
                                                <Skeleton className={`w-full h-full absolute left-0 top-0 rounded-lg`} />
                                                <Spinner size={40} className="text-pink-600" />
                                            </div>
                                        }
                                        {isLoading && n > 4 && 
                                            <div className="relative flex items-center justify-center !aspect-square">
                                                <Skeleton className={`w-full h-full absolute left-0 top-0 rounded-lg`} />
                                                <Spinner size={40} className="text-pink-600" />
                                            </div>
                                        }
                                        {historyImages?.map((result, key) => {
                                            result = {
                                                ...result,
                                                images: result?.images?.filter(e => e?.status == "analyzed")
                                            };
                                            const image = result?.images?.[0];
                                            const imageURL = result?.images?.[0]?.url || result?.response?.split(",")?.[0];
                                            const imageThumbnailURL = result?.images?.[0]?.thumbnail?.url || imageURL;
                                            return (
                                                <span 
                                                    key={key}
                                                    className={`${!!showPreview && `transition-all overflow-hidden`}`}
                                                >
                                                    <MediaElement 
                                                        key={imageURL}
                                                        src={imageURL}
                                                        originalSrc={imageURL}
                                                        handleOnSelect={handleOnSelect}
                                                        handleOnUnselect={handleOnUnselect}
                                                        selected={!!selectedImages?.find(e => e.originalSrc == imageURL)} 
                                                        isSelectable={isSelectable}
                                                        aspectRatio={getAspectRatio(image?.width, image?.height)}
                                                        isEditable={true}
                                                        mediaDescription={`Prompt: "${result?.query}"`}
                                                        aiImageModel={result?.model}
                                                        user={result?.user}
                                                        onError={(e) => {
                                                            try { e.target.parentElement.parentElement.style = "display: none" } catch (err) {}
                                                        }}
                                                    />
                                                </span>
                                            );
                                        })}
                                    </Masonry>
                                </ResponsiveMasonry>
                            )
                        }
                        {!!nextLink && 
                            <div className="w-full flex justify-center mt-6">
                                <Button 
                                    variant="solid"
                                    onClick={() => getHistoryImages(false)}
                                >
                                    Load more
                                </Button>
                            </div>
                        }
                    </div>
                </Container>
            }

            <ImageSelector
                isOpen={showImageSelector}
                onClose={() => setShowImageSelector(false)}
                onSelect={handleSelectImages}
                maxImages={maxInputImages - selectedInputImages.length}
                allowedTypes={["image"]}
            />
        </div>
    )
}

export default AIImageBody;
